'use client';

// ── Exchange-rate provider ────────────────────────────────────────────────
// Resolves the USDC→local-currency exchange-rate table ONCE for the whole app
// and shares it via context.
//
// Why a context for SDK-backed data (a deviation from the three-tier model's
// "SDK hooks at component level" default): the rate table is a single global,
// effectively immutable value — `useGetExchangeRates` is a non-realtime
// `kind: single` query with a 60s staleTime (see sdk.manifest.yml). Before this
// provider, `useFormattedCurrency()` called `useGetExchangeRates` internally,
// and ~1590 money-displaying leaves on the `/markets` feed each spun up their
// own SDK query observer for that one value (measured: 1590 live instances,
// 67 commits/sec). Hoisting the single hook call here collapses that to ONE
// observer; every `useFormattedCurrency()` caller reads the shared context
// instead. SSR still hydrates the query (see `lib/sdk/ssr/shell.ts`), so this
// provider sees the prefetched data on first paint.
//
// The provider issues exactly one `useGetExchangeRates` observer for the whole
// app; the context value object is memoised automatically by the React
// Compiler (no manual `useMemo` — manual memo hooks are banned here), so
// consuming it adds no re-renders beyond an actual rates/loading change.
import { c as _c } from "react/compiler-runtime";
import { useGetExchangeRates } from '@morfi/backend-sdk-v2/client';
import { type Context, createContext, type ReactNode, use } from 'react';
export interface ExchangeRateContextValue {
  /** USDC→local-currency rates keyed by ISO currency code. `undefined` until loaded. */
  rates: Record<string, number> | undefined;
  /** `true` while the single app-wide rates query is still pending. */
  isLoading: boolean;
}
const FALLBACK: ExchangeRateContextValue = {
  rates: undefined,
  isLoading: false
};
const ExchangeRateContext: Context<ExchangeRateContextValue> = createContext<ExchangeRateContextValue>(FALLBACK);

/**
 * Mount once near the app root (inside `<SDKProvider>`). Issues the single
 * `useGetExchangeRates` call for the entire app.
 */
export function ExchangeRateProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fields: ["rates"]
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data,
    isPending
  } = useGetExchangeRates(t1);
  const rates = data?.rates as Record<string, number> | undefined;
  let t2;
  if ($[1] !== isPending || $[2] !== rates) {
    t2 = {
      rates,
      isLoading: isPending
    };
    $[1] = isPending;
    $[2] = rates;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const value = t2;
  let t3;
  if ($[4] !== children || $[5] !== value) {
    t3 = <ExchangeRateContext.Provider value={value}>{children}</ExchangeRateContext.Provider>;
    $[4] = children;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Read the app-wide exchange-rate table. Returns the shared context value —
 * does NOT create a new SDK query observer.
 */
export function useExchangeRates(): ExchangeRateContextValue {
  return use(ExchangeRateContext);
}